import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, MousePointer2, Search, Camera, MessageSquare, Sparkles, MapPin, NotebookPen, Gamepad2, Zap, Blocks, ListChecks, UserPlus, Building2, Wand2, Trophy, ShoppingBag, Heart, LayoutGrid } from "lucide-react";

/* ─── Reusable primitives ──────────────────────────────────────────── */
const Cursor = ({ x, y, delay = 0 }) => (
  <motion.div
    initial={{ x: 0, y: 0, opacity: 0 }}
    animate={{ x, y, opacity: [0, 1, 1, 1, 0] }}
    transition={{ duration: 3, delay, times: [0, 0.15, 0.6, 0.85, 1], repeat: Infinity, repeatDelay: 0.5 }}
    className="absolute z-30 pointer-events-none top-0 left-0"
  >
    <MousePointer2 className="w-5 h-5 text-[#1F3A5F] drop-shadow" fill="#fff" />
  </motion.div>
);

const Screen = ({ children, className = "" }) => (
  <div className={`absolute inset-0 bg-white rounded-2xl border border-[#1F3A5F]/10 overflow-hidden ${className}`}>
    {children}
  </div>
);

const TopBar = ({ title }) => (
  <div className="h-8 bg-[#FBF7F3] border-b border-[#1F3A5F]/10 flex items-center px-3 gap-1.5">
    <span className="w-2 h-2 rounded-full bg-[#E5276A]/40" />
    <span className="w-2 h-2 rounded-full bg-[#F9C548]/50" />
    <span className="w-2 h-2 rounded-full bg-[#4CAF50]/40" />
    <span className="ml-3 text-[10px] font-bold text-[#1F3A5F]/60 truncate">{title}</span>
  </div>
);

/* ─── Scene variants ──────────────────────────────────────────────── */

/** Chat / Coach IA */
const ChatScene = () => (
  <Screen>
    <TopBar title="geoVINUM AI" />
    <div className="p-4 space-y-2 h-[calc(100%-2rem)] flex flex-col justify-end">
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.3, repeat: Infinity, repeatDelay: 2.5 }}
        className="self-end max-w-[70%] rounded-2xl rounded-br-sm bg-[#E5276A] text-white px-3 py-2 text-xs">
        Différence Syrah / Shiraz ?
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0] }}
        transition={{ duration: 3, times: [0, 0.1, 0.9, 1], delay: 0.8, repeat: Infinity, repeatDelay: 0 }}
        className="self-start flex gap-1 px-3 py-2 bg-[#FBF7F3] rounded-2xl rounded-bl-sm w-fit">
        {[0, 1, 2].map((i) => (
          <motion.span key={i} className="w-1.5 h-1.5 rounded-full bg-[#1F3A5F]/50"
            animate={{ y: [0, -3, 0] }}
            transition={{ duration: 0.6, delay: i * 0.15, repeat: Infinity }} />
        ))}
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 1.8, repeat: Infinity, repeatDelay: 1.5 }}
        className="self-start max-w-[80%] rounded-2xl rounded-bl-sm bg-[#FBF7F3] px-3 py-2 text-xs text-[#1F3A5F]">
        Même cépage. Syrah = France (élégant, poivré). Shiraz = Australie (puissant, fruits noirs).
      </motion.div>
    </div>
  </Screen>
);

/** Vision IA — Photo identify */
const PhotoScene = () => (
  <Screen>
    <TopBar title="Explorer · Vision IA" />
    <div className="relative h-[calc(100%-2rem)] flex flex-col items-center justify-center gap-3 p-4">
      <motion.div className="relative w-24 h-32 rounded-lg bg-gradient-to-br from-[#8B0000] to-[#4A0010] shadow-lg overflow-hidden"
        animate={{ scale: [1, 1.02, 1] }} transition={{ duration: 2, repeat: Infinity }}>
        <div className="absolute inset-x-2 top-6 h-10 bg-[#FBF7F3] rounded-sm flex items-center justify-center">
          <span className="text-[8px] font-display text-[#1F3A5F]">Château</span>
        </div>
        <motion.div className="absolute inset-0 border-2 border-[#E5276A]"
          animate={{ opacity: [0, 1, 1, 0] }} transition={{ duration: 3, times: [0, 0.3, 0.7, 1], repeat: Infinity }} />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: [0, 1, 1, 0], y: [10, 0, 0, -10] }}
        transition={{ duration: 3, times: [0, 0.3, 0.8, 1], delay: 1.2, repeat: Infinity }}
        className="w-full max-w-[220px] p-2 rounded-lg bg-[#FBF7F3] border border-[#E5276A]/30">
        <div className="text-[9px] font-bold text-[#E5276A] uppercase tracking-wider">Identifié</div>
        <div className="text-[10px] font-display text-[#1F3A5F]">Pauillac · Cabernet Sauvignon</div>
      </motion.div>
      <Camera className="w-4 h-4 text-[#8B95A1] absolute bottom-3" />
    </div>
  </Screen>
);

/** Atlas — appellation/cépage/producer page */
const AtlasScene = () => (
  <Screen>
    <TopBar title="/appellations/chablis" />
    <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-2">
      <div className="h-16 rounded-lg bg-gradient-to-br from-[#F9C548]/40 via-[#FBF7F3] to-[#E5276A]/20 relative overflow-hidden">
        <div className="absolute inset-2 flex items-end">
          <span className="text-[10px] font-display text-[#1F3A5F]">Chablis · Bourgogne</span>
        </div>
      </div>
      <div className="flex gap-1 overflow-hidden">
        {["Histoire", "Climat", "Cépages", "Terroir"].map((t, i) => (
          <motion.div key={t}
            initial={{ opacity: 0.3 }}
            animate={{ opacity: [0.3, 1, 1, 0.3] }}
            transition={{ duration: 4, delay: i * 0.4, times: [0, 0.2, 0.8, 1], repeat: Infinity }}
            className="text-[8px] px-2 py-1 rounded-full bg-[#FBF7F3] font-bold text-[#1F3A5F] whitespace-nowrap">
            {t}
          </motion.div>
        ))}
      </div>
      <div className="flex-1 space-y-1.5">
        {[0, 1, 2].map((i) => (
          <motion.div key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 + i * 0.3, repeat: Infinity, repeatDelay: 2.5 }}
            className="p-2 rounded-lg bg-[#FBF7F3] border border-[#1F3A5F]/10">
            <div className="h-1.5 rounded-full bg-[#1F3A5F]/20 w-3/4" />
            <div className="h-1 rounded-full bg-[#1F3A5F]/10 mt-1 w-1/2" />
          </motion.div>
        ))}
      </div>
    </div>
  </Screen>
);

/** Atlas Cépages — fiche cépage */
const CepageScene = () => (
  <Screen>
    <TopBar title="/cepages/pinot-noir" />
    <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-2">
      <div className="h-16 rounded-lg bg-gradient-to-br from-[#8B0000]/30 via-[#FBF7F3] to-[#E5276A]/25 relative overflow-hidden">
        <div className="absolute inset-2 flex items-end justify-between">
          <span className="text-[10px] font-display text-[#1F3A5F]">Pinot noir · Rouge</span>
          <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-white/80 text-[#8B0000] font-bold">🍇</span>
        </div>
      </div>
      <div className="flex gap-1 overflow-hidden flex-wrap">
        {["Origine", "Arômes", "Climat", "Régions"].map((t, i) => (
          <motion.div key={t}
            initial={{ opacity: 0.3 }}
            animate={{ opacity: [0.3, 1, 1, 0.3] }}
            transition={{ duration: 4, delay: i * 0.4, times: [0, 0.2, 0.8, 1], repeat: Infinity }}
            className="text-[8px] px-2 py-1 rounded-full bg-[#FBF7F3] font-bold text-[#1F3A5F] whitespace-nowrap">
            {t}
          </motion.div>
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6, repeat: Infinity, repeatDelay: 2.5 }}
        className="p-2 rounded-lg bg-[#FBF7F3] border border-[#1F3A5F]/10">
        <div className="text-[8px] font-bold text-[#1F3A5F]">Profil aromatique</div>
        <div className="mt-1 flex gap-1 flex-wrap">
          {["Cerise", "Fraise", "Sous-bois", "Épices"].map((a) => (
            <span key={a} className="text-[7px] px-1.5 py-0.5 rounded bg-white text-[#1F3A5F]/70">{a}</span>
          ))}
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0, repeat: Infinity, repeatDelay: 2.5 }}
        className="p-2 rounded-lg bg-[#FBF7F3] border border-[#1F3A5F]/10 flex-1">
        <div className="text-[8px] font-bold text-[#1F3A5F]">Régions emblématiques</div>
        <div className="mt-1 space-y-0.5">
          <div className="h-1 rounded-full bg-[#8B0000]/40 w-4/5" />
          <div className="h-1 rounded-full bg-[#8B0000]/30 w-3/5" />
          <div className="h-1 rounded-full bg-[#8B0000]/20 w-2/5" />
        </div>
      </motion.div>
    </div>
  </Screen>
);

/** Atlas Producteurs — annuaire domaine */
const ProducerScene = () => (
  <Screen>
    <TopBar title="/producers/domaine-leroy" />
    <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-2">
      <div className="h-16 rounded-lg bg-gradient-to-br from-[#1F3A5F]/25 via-[#FBF7F3] to-[#F9C548]/30 relative overflow-hidden">
        <div className="absolute inset-2 flex items-end justify-between">
          <div>
            <div className="text-[10px] font-display text-[#1F3A5F] leading-tight">Domaine Leroy</div>
            <div className="text-[7px] text-[#8B95A1]">Vosne-Romanée · Bourgogne</div>
          </div>
          <span className="text-[7px] px-1.5 py-0.5 rounded-full bg-[#E5276A] text-white font-bold">PRO</span>
        </div>
      </div>
      <div className="flex gap-1 overflow-hidden">
        {["Histoire", "Cuvées", "Visite", "Contact"].map((t, i) => (
          <motion.div key={t}
            initial={{ opacity: 0.3 }}
            animate={{ opacity: [0.3, 1, 1, 0.3] }}
            transition={{ duration: 4, delay: i * 0.4, times: [0, 0.2, 0.8, 1], repeat: Infinity }}
            className="text-[8px] px-2 py-1 rounded-full bg-[#FBF7F3] font-bold text-[#1F3A5F] whitespace-nowrap">
            {t}
          </motion.div>
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.6, repeat: Infinity, repeatDelay: 2.5 }}
        className="p-2 rounded-lg bg-[#FBF7F3] border border-[#1F3A5F]/10">
        <div className="text-[8px] font-bold text-[#1F3A5F]">Cuvées phares</div>
        <div className="mt-1 flex gap-1 flex-wrap">
          <span className="text-[7px] px-1.5 py-0.5 rounded bg-white text-[#8B0000]">Musigny GC</span>
          <span className="text-[7px] px-1.5 py-0.5 rounded bg-white text-[#8B0000]">Richebourg GC</span>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 1.0, repeat: Infinity, repeatDelay: 2.5 }}
        className="p-2 rounded-lg bg-[#FBF7F3] border border-[#1F3A5F]/10 flex-1 flex items-center gap-2">
        <MapPin className="w-3 h-3 text-[#E5276A] shrink-0" />
        <div className="flex-1">
          <div className="text-[8px] font-bold text-[#1F3A5F]">Visites œnotouristiques</div>
          <div className="text-[7px] text-[#4A5568]">Sur RDV · Dégustation 4 cuvées</div>
        </div>
      </motion.div>
    </div>
  </Screen>
);

/** Carnet — free text → structured */
const NotebookScene = () => (
  <Screen>
    <TopBar title="Carnet · Nouvelle dégustation" />
    <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-2">
      <motion.div className="rounded-lg border border-[#1F3A5F]/15 p-2 text-[9px] text-[#4A5568] italic min-h-[3rem]"
        initial={{ opacity: 0.4 }} animate={{ opacity: [0.4, 1, 1, 0.4] }}
        transition={{ duration: 4, times: [0, 0.25, 0.5, 1], repeat: Infinity }}>
        {'"Robe grenat profonde, nez sur les fruits noirs, tanins soyeux, finale poivrée..."'}
      </motion.div>
      <motion.div className="text-center"
        initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 1, 0] }}
        transition={{ duration: 4, times: [0, 0.5, 0.6, 0.7], repeat: Infinity }}>
        <Sparkles className="w-4 h-4 text-[#E5276A] inline-block" />
        <span className="text-[9px] text-[#E5276A] ml-1 font-bold">IA structuration…</span>
      </motion.div>
      <motion.div className="rounded-lg bg-gradient-to-br from-[#8B0000]/10 to-[#FBF7F3] border border-[#E5276A]/20 p-2"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: [0, 0, 1, 1, 0], y: [10, 10, 0, 0, -5] }}
        transition={{ duration: 4, times: [0, 0.7, 0.8, 0.95, 1], repeat: Infinity }}>
        <div className="text-[9px] font-display text-[#1F3A5F]">Cornas 2019 · Syrah</div>
        <div className="flex gap-1 mt-1">
          <span className="text-[7px] px-1.5 py-0.5 rounded bg-white text-[#1F3A5F]/70">Robe grenat</span>
          <span className="text-[7px] px-1.5 py-0.5 rounded bg-white text-[#1F3A5F]/70">Fruits noirs</span>
        </div>
        <div className="mt-1 flex justify-between items-center">
          <div className="flex gap-0.5">
            {[1, 2, 3, 4].map((s) => <div key={s} className="w-1.5 h-1.5 rounded-full bg-[#E5276A]" />)}
            <div className="w-1.5 h-1.5 rounded-full bg-[#1F3A5F]/20" />
          </div>
          <span className="text-[8px] font-bold text-[#E5276A]">4/5</span>
        </div>
      </motion.div>
    </div>
  </Screen>
);

/** Map — nearby / itinerary / map-builder */
const MapScene = () => (
  <Screen>
    <TopBar title="Around Me" />
    <div className="h-[calc(100%-2rem)] relative bg-gradient-to-br from-[#E8F1F8] via-[#F0EAD6] to-[#D9E8DB] overflow-hidden">
      {/* Fake road lines */}
      <svg className="absolute inset-0 w-full h-full opacity-40">
        <path d="M 0 40 Q 100 20 200 60 T 400 80" stroke="#8B95A1" strokeWidth="1" fill="none" />
        <path d="M 0 100 Q 80 80 160 120 T 320 140" stroke="#8B95A1" strokeWidth="1" fill="none" />
      </svg>
      {[
        { x: "20%", y: "30%", delay: 0.2 },
        { x: "55%", y: "50%", delay: 0.7 },
        { x: "75%", y: "25%", delay: 1.2 },
        { x: "35%", y: "70%", delay: 1.7 },
      ].map((p, i) => (
        <motion.div key={i} className="absolute -translate-x-1/2 -translate-y-full"
          style={{ left: p.x, top: p.y }}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: p.delay, repeat: Infinity, repeatDelay: 2 }}>
          <MapPin className="w-6 h-6 text-[#E5276A] drop-shadow" fill="#E5276A" strokeWidth={1.5} />
        </motion.div>
      ))}
      <div className="absolute bottom-2 left-2 right-2 flex gap-1">
        <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-white/90 font-bold text-[#1F3A5F]">Domaines</span>
        <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-white/70 text-[#8B95A1]">Cavistes</span>
      </div>
    </div>
  </Screen>
);

/** Game / Quiz / Deduce */
const GameScene = () => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setPhase((p) => (p + 1) % 3), 1500);
    return () => clearInterval(timer);
  }, []);
  return (
    <Screen>
      <TopBar title="Play · Quiz" />
      <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-2">
        <div className="text-[8px] font-bold text-[#8B95A1] uppercase tracking-wider">Question 3/10</div>
        <div className="text-[10px] font-display text-[#1F3A5F] leading-tight">
          Quel cépage domine à Chablis ?
        </div>
        <div className="grid grid-cols-2 gap-1.5 mt-1">
          {["Chardonnay", "Riesling", "Sauvignon", "Aligoté"].map((opt, i) => {
            const isRight = i === 0;
            const revealed = phase >= 1;
            const picked = phase >= 2 && i === 0;
            return (
              <motion.div key={opt}
                animate={{
                  backgroundColor: picked ? "#4CAF50" : revealed && isRight ? "#E5276A" : "#FBF7F3",
                  scale: picked ? [1, 1.05, 1] : 1,
                }}
                transition={{ duration: 0.3 }}
                className="rounded-lg p-1.5 border border-[#1F3A5F]/10 text-[9px] text-[#1F3A5F] text-center font-bold">
                <span style={{ color: picked || (revealed && isRight) ? "#fff" : undefined }}>{opt}</span>
              </motion.div>
            );
          })}
        </div>
        <div className="mt-auto flex items-center gap-1">
          <Trophy className="w-3 h-3 text-[#F9C548]" />
          <span className="text-[8px] font-bold text-[#1F3A5F]">+50 XP</span>
        </div>
      </div>
    </Screen>
  );
};

/** Flashcard flip */
const FlashcardScene = () => {
  const [flipped, setFlipped] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => setFlipped((f) => !f), 2000);
    return () => clearInterval(timer);
  }, []);
  return (
    <Screen>
      <TopBar title="Microlearning" />
      <div className="h-[calc(100%-2rem)] p-4 flex items-center justify-center">
        <motion.div className="w-full max-w-[200px] aspect-[4/3] rounded-xl bg-gradient-to-br from-[#1F3A5F] to-[#2A4870] text-white flex items-center justify-center p-3 shadow-lg"
          animate={{ rotateY: flipped ? 180 : 0 }} transition={{ duration: 0.6 }}
          style={{ transformStyle: "preserve-3d" }}>
          <div className="absolute inset-0 flex items-center justify-center p-3" style={{ backfaceVisibility: "hidden" }}>
            <span className="font-display text-sm text-center">Nebbiolo ?</span>
          </div>
          <div className="absolute inset-0 flex items-center justify-center p-3 bg-[#E5276A] rounded-xl"
               style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
            <span className="text-[10px] text-center leading-snug">Cépage roi du Piémont (Barolo, Barbaresco)</span>
          </div>
        </motion.div>
      </div>
    </Screen>
  );
};

/** Course / Academy */
const CourseScene = () => (
  <Screen>
    <TopBar title="Academy · Chapitre 3" />
    <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-2">
      <div className="text-[8px] font-bold text-[#E5276A] uppercase tracking-wider">Bourgogne · Explorer</div>
      <div className="text-[11px] font-display text-[#1F3A5F] leading-tight">Le climat de la Côte de Nuits</div>
      <div className="flex gap-1 items-center">
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.div key={i}
            className="h-1 rounded-full flex-1"
            initial={{ backgroundColor: i < 2 ? "#E5276A" : "#E5E7EB" }}
            animate={{ backgroundColor: i <= 2 ? "#E5276A" : "#E5E7EB" }}
            transition={{ duration: 1, delay: i * 0.3, repeat: Infinity, repeatDelay: 3 }} />
        ))}
      </div>
      <div className="flex-1 space-y-1">
        <div className="h-1.5 rounded-full bg-[#1F3A5F]/15 w-full" />
        <div className="h-1.5 rounded-full bg-[#1F3A5F]/15 w-11/12" />
        <div className="h-1.5 rounded-full bg-[#1F3A5F]/15 w-9/12" />
      </div>
      <motion.div className="absolute bottom-2 right-2 rounded-full bg-[#E5276A] w-8 h-8 flex items-center justify-center shadow-lg"
        animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
        <Sparkles className="w-4 h-4 text-white" />
      </motion.div>
    </div>
  </Screen>
);

/** Suggestions / taste profile */
const SuggestionsScene = () => (
  <Screen>
    <TopBar title="Suggestions IA" />
    <div className="h-[calc(100%-2rem)] p-3 grid grid-cols-3 gap-1.5">
      {[
        { color: "#8B0000", label: "Similaire", match: 92 },
        { color: "#F9C548", label: "Découverte", match: 78 },
        { color: "#E5276A", label: "Coup de ♥", match: 88 },
      ].map((w, i) => (
        <motion.div key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: i * 0.3, repeat: Infinity, repeatDelay: 2 }}
          className="flex flex-col items-center gap-1">
          <div className="w-8 h-12 rounded-t-full rounded-b" style={{ backgroundColor: w.color }} />
          <span className="text-[7px] font-bold text-[#1F3A5F]">{w.label}</span>
          <span className="text-[9px] font-display text-[#E5276A]">{w.match}%</span>
        </motion.div>
      ))}
    </div>
  </Screen>
);

/** Admin — Generate blocks (progress) */
const GenerateScene = () => (
  <Screen>
    <TopBar title="Admin · Atlas Blocks" />
    <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-2">
      <div className="text-[9px] font-bold text-[#E5276A] uppercase tracking-wider">Génération asynchrone</div>
      <div className="text-[10px] font-display text-[#1F3A5F]">aloxe-corton · 20 catégories</div>
      <div className="h-2 rounded-full bg-[#1F3A5F]/10 overflow-hidden">
        <motion.div className="h-full bg-gradient-to-r from-[#E5276A] to-[#F9C548] rounded-full"
          initial={{ width: "0%" }} animate={{ width: "100%" }}
          transition={{ duration: 4, ease: "linear", repeat: Infinity }} />
      </div>
      <div className="grid grid-cols-4 gap-1">
        {["Histoire", "Climat", "Terroir", "Cépages", "Vinif.", "Accords", "Anecdotes", "Chiffres"].map((c, i) => (
          <motion.div key={c}
            initial={{ opacity: 0.2, scale: 0.9 }}
            animate={{ opacity: [0.2, 0.2, 1, 1], scale: [0.9, 0.9, 1, 1] }}
            transition={{ duration: 4, times: [0, i * 0.1, i * 0.1 + 0.05, 1], repeat: Infinity }}
            className="text-[7px] px-1 py-0.5 rounded bg-[#FBF7F3] font-bold text-[#1F3A5F] text-center truncate">
            {c}
          </motion.div>
        ))}
      </div>
    </div>
  </Screen>
);

/** Admin — Bulk publish */
const BulkScene = () => (
  <Screen>
    <TopBar title="Admin · Bulk Publish" />
    <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-1.5">
      {[0, 1, 2, 3].map((i) => (
        <motion.div key={i}
          initial={{ opacity: 1 }}
          className="flex items-center gap-2 p-1.5 rounded-lg bg-[#FBF7F3]">
          <motion.div
            initial={{ backgroundColor: "#fff", borderColor: "#8B95A1" }}
            animate={{ backgroundColor: ["#fff", "#E5276A"], borderColor: ["#8B95A1", "#E5276A"] }}
            transition={{ duration: 0.3, delay: 0.5 + i * 0.2, repeat: Infinity, repeatDelay: 3 }}
            className="w-3 h-3 rounded border-2 flex items-center justify-center">
            <motion.svg width="8" height="8" viewBox="0 0 8 8" fill="none"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1] }}
              transition={{ duration: 0.2, delay: 0.6 + i * 0.2, repeat: Infinity, repeatDelay: 3 }}>
              <path d="M1 4 L3 6 L7 2" stroke="#fff" strokeWidth="1.5" />
            </motion.svg>
          </motion.div>
          <div className="h-1.5 rounded-full bg-[#1F3A5F]/20 flex-1" />
          <span className="text-[7px] px-1 py-0.5 rounded bg-white font-bold text-[#8B95A1]">Premium</span>
        </motion.div>
      ))}
      <motion.div className="mt-1 mx-auto px-3 py-1 rounded-lg bg-[#E5276A] text-white text-[8px] font-bold"
        animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 0.4, delay: 1.4, repeat: Infinity, repeatDelay: 3 }}>
        Rendre public
      </motion.div>
    </div>
  </Screen>
);

/** Story Builder — sections regenerating */
const StoryScene = () => (
  <Screen>
    <TopBar title="Pro · Story Builder" />
    <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-1.5">
      {["Histoire", "Terroir", "Philosophie"].map((s, i) => (
        <motion.div key={s}
          className="p-2 rounded-lg bg-gradient-to-r from-[#FBF7F3] to-white border border-[#1F3A5F]/10 flex items-center justify-between"
          animate={i === 1 ? { borderColor: ["#1F3A5F1a", "#E5276A", "#1F3A5F1a"] } : {}}
          transition={{ duration: 2, delay: 0.5, repeat: Infinity, repeatDelay: 2 }}>
          <div>
            <div className="text-[9px] font-display text-[#1F3A5F]">{s}</div>
            <div className="h-1 rounded-full bg-[#1F3A5F]/15 w-16 mt-0.5" />
          </div>
          {i === 1 && (
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 1.5, delay: 0.8, repeat: Infinity, repeatDelay: 2.5, ease: "linear" }}>
              <Sparkles className="w-3 h-3 text-[#E5276A]" />
            </motion.div>
          )}
        </motion.div>
      ))}
    </div>
  </Screen>
);

/** Signup */
const SignupScene = () => (
  <Screen>
    <TopBar title="Créer un compte" />
    <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-2 justify-center">
      {[
        { label: "Nom", chars: "Camille" },
        { label: "Email", chars: "camille@…" },
        { label: "Mot de passe", chars: "••••••••" },
      ].map((f, i) => (
        <div key={f.label} className="space-y-0.5">
          <div className="text-[7px] font-bold text-[#8B95A1] uppercase tracking-wider">{f.label}</div>
          <motion.div className="h-5 rounded border border-[#1F3A5F]/15 bg-white flex items-center px-2 text-[9px] text-[#1F3A5F] overflow-hidden">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 + i * 0.6, repeat: Infinity, repeatDelay: 2 }}>
              {f.chars}
            </motion.span>
          </motion.div>
        </div>
      ))}
      <motion.div className="mt-1 mx-auto px-4 py-1.5 rounded-lg bg-[#E5276A] text-white text-[9px] font-bold shadow"
        animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 0.5, delay: 2.5, repeat: Infinity, repeatDelay: 2 }}>
        Créer mon compte
      </motion.div>
    </div>
  </Screen>
);

/** Shop */
const ShopScene = () => (
  <Screen>
    <TopBar title="Boutique geoVINUM" />
    <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        {[0, 1].map((i) => (
          <div key={i} className="rounded-lg bg-[#FBF7F3] p-2 flex flex-col items-center gap-1">
            <div className="w-full aspect-square rounded bg-gradient-to-br from-[#E5276A]/20 to-[#F9C548]/30" />
            <div className="text-[8px] font-display text-[#1F3A5F]">Carte {i === 0 ? "Bourgogne" : "Bordeaux"}</div>
            <div className="text-[8px] font-bold text-[#E5276A]">39 €</div>
          </div>
        ))}
      </div>
      <motion.div className="mt-auto flex items-center justify-between p-2 rounded-lg bg-[#1F3A5F] text-white"
        animate={{ scale: [1, 1.03, 1] }} transition={{ duration: 0.4, delay: 1, repeat: Infinity, repeatDelay: 2.5 }}>
        <div className="flex items-center gap-1">
          <ShoppingBag className="w-3 h-3" />
          <span className="text-[9px]">Panier · 2 articles</span>
        </div>
        <span className="text-[9px] font-bold">78 €</span>
      </motion.div>
    </div>
  </Screen>
);

/** Deduce — questions guided */
const DeduceScene = () => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setStep((s) => (s + 1) % 4), 1200);
    return () => clearInterval(timer);
  }, []);
  const questions = [
    { q: "La robe est…", a: ["Claire", "Grenat", "Tuilée"] },
    { q: "Au nez…", a: ["Fruits rouges", "Fruits noirs", "Épices"] },
    { q: "En bouche…", a: ["Léger", "Corsé", "Tannique"] },
    { q: "Verdict IA", a: ["🍷 Bordeaux · Cabernet"] },
  ];
  const cur = questions[step];
  return (
    <Screen>
      <TopBar title="Déduire mon vin" />
      <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-2 items-center justify-center">
        <div className="text-[8px] font-bold text-[#8B95A1] uppercase tracking-wider">Étape {step + 1}/4</div>
        <motion.div key={step} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="text-[10px] font-display text-[#1F3A5F]">{cur.q}</div>
          <div className="flex gap-1 mt-2 flex-wrap justify-center">
            {cur.a.map((a, i) => (
              <span key={i} className="text-[8px] px-1.5 py-0.5 rounded-full bg-[#FBF7F3] font-bold text-[#1F3A5F]">
                {a}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </Screen>
  );
};

/** Nearby — same as MapScene but keep as its own for clarity */
const NearbyScene = MapScene;

/** Coach — coach personal */
const CoachScene = () => (
  <Screen>
    <TopBar title="Coach personnel" />
    <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-2">
      <div className="text-[9px] font-bold text-[#E5276A] uppercase tracking-wider">Ton plan hebdo</div>
      {[
        { label: "Bordeaux — Rive gauche", pct: 80 },
        { label: "Cépages du monde", pct: 50 },
        { label: "Accords fromages", pct: 25 },
      ].map((p, i) => (
        <motion.div key={p.label}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: i * 0.3, repeat: Infinity, repeatDelay: 2.5 }}
          className="space-y-0.5">
          <div className="flex justify-between text-[8px]">
            <span className="text-[#1F3A5F] font-bold">{p.label}</span>
            <span className="text-[#E5276A] font-bold">{p.pct}%</span>
          </div>
          <div className="h-1 rounded-full bg-[#1F3A5F]/10">
            <motion.div className="h-full rounded-full bg-gradient-to-r from-[#E5276A] to-[#F9C548]"
              initial={{ width: 0 }}
              animate={{ width: `${p.pct}%` }}
              transition={{ duration: 1, delay: i * 0.3 + 0.2, repeat: Infinity, repeatDelay: 2.5 }} />
          </div>
        </motion.div>
      ))}
    </div>
  </Screen>
);

/** Map Builder */
const MapBuilderScene = () => (
  <Screen>
    <TopBar title="Pro · Map Builder" />
    <div className="h-[calc(100%-2rem)] relative bg-gradient-to-br from-[#F0EAD6] via-[#E8DFB8] to-[#D9C88A] overflow-hidden">
      <svg className="absolute inset-0 w-full h-full">
        {[
          { d: "M 20 30 L 80 20 L 100 60 L 30 70 Z", color: "#E5276A" },
          { d: "M 100 60 L 160 40 L 170 90 L 110 100 Z", color: "#8B0000" },
          { d: "M 30 70 L 110 100 L 90 140 L 20 130 Z", color: "#F9C548" },
        ].map((p, i) => (
          <motion.path key={i} d={p.d}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ duration: 0.5, delay: i * 0.4, repeat: Infinity, repeatDelay: 2.5 }}
            fill={p.color} stroke="#1F3A5F" strokeWidth="1" />
        ))}
      </svg>
      <div className="absolute bottom-2 left-2 text-[8px] font-bold text-[#1F3A5F] bg-white/80 px-1.5 py-0.5 rounded">
        Parcellaire IA
      </div>
    </div>
  </Screen>
);

/** Sponsored quiz builder */
const SponsoredScene = () => (
  <Screen>
    <TopBar title="Quiz sponsorisé" />
    <div className="h-[calc(100%-2rem)] p-3 flex flex-col gap-1.5">
      <div className="text-[9px] font-bold text-[#E5276A] uppercase tracking-wider">Nouveau jeu</div>
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: "QR Code", el: (
            <div className="w-8 h-8 grid grid-cols-4 gap-px bg-white">
              {Array.from({ length: 16 }).map((_, i) => (
                <div key={i} className={i % 3 === 0 || i === 5 || i === 10 ? "bg-[#1F3A5F]" : "bg-white"} />
              ))}
            </div>
          )},
          { label: "Récompense", el: <span className="text-lg">🎁</span> },
        ].map((c) => (
          <div key={c.label} className="rounded-lg bg-[#FBF7F3] p-2 flex flex-col items-center gap-1">
            {c.el}
            <span className="text-[8px] font-bold text-[#1F3A5F]">{c.label}</span>
          </div>
        ))}
      </div>
      <motion.div className="mt-auto p-1.5 rounded-lg bg-[#1F3A5F] text-white text-[8px] font-bold text-center"
        animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 2, repeat: Infinity }}>
        12 leads collectées
      </motion.div>
    </div>
  </Screen>
);

/** Default fallback */
const DefaultScene = () => (
  <Screen>
    <TopBar title="geoVINUM" />
    <div className="h-[calc(100%-2rem)] flex items-center justify-center relative">
      <motion.div className="px-4 py-2 rounded-lg bg-[#E5276A] text-white text-xs font-bold shadow"
        animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
        Découvrir
      </motion.div>
      <Cursor x={70} y={30} delay={0.5} />
    </div>
  </Screen>
);

/* ─── Variant registry ────────────────────────────────────────────── */
const VARIANTS = {
  chat: ChatScene,
  photo: PhotoScene,
  atlas: AtlasScene,
  cepage: CepageScene,
  producer: ProducerScene,
  notebook: NotebookScene,
  map: MapScene,
  nearby: NearbyScene,
  game: GameScene,
  flashcard: FlashcardScene,
  course: CourseScene,
  suggestions: SuggestionsScene,
  coach: CoachScene,
  deduce: DeduceScene,
  signup: SignupScene,
  shop: ShopScene,
  "generate-blocks": GenerateScene,
  "bulk-toggle": BulkScene,
  story: StoryScene,
  "map-builder": MapBuilderScene,
  sponsored: SponsoredScene,
  default: DefaultScene,
};

const VARIANT_ICON = {
  chat: MessageSquare, coach: Sparkles, photo: Camera, atlas: Search,
  cepage: LayoutGrid, producer: Building2,
  notebook: NotebookPen, map: MapPin, nearby: MapPin, game: Gamepad2,
  flashcard: Zap, course: Sparkles, suggestions: Heart, deduce: Sparkles,
  signup: UserPlus, shop: ShoppingBag, "generate-blocks": Blocks,
  "bulk-toggle": ListChecks, story: Wand2, "map-builder": MapPin,
  sponsored: Building2, default: Play,
};

/* ─── Thumbnail + Modal ────────────────────────────────────────────── */
export function DemoThumbnail({ variant, onClick, lang }) {
  const SceneComp = VARIANTS[variant] || DefaultScene;
  const Icon = VARIANT_ICON[variant] || Play;
  return (
    <button
      onClick={onClick}
      data-testid={`demo-thumbnail-${variant}`}
      className="group relative w-full max-w-[220px] aspect-[4/3] rounded-xl overflow-hidden border border-[#1F3A5F]/15 shadow-sm hover:shadow-lg hover:border-[#E5276A]/40 transition-all"
    >
      <div className="absolute inset-0 pointer-events-none scale-[0.95] origin-center">
        <SceneComp />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#1F3A5F]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-white opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-[#E5276A]">
          <Play className="w-3 h-3" fill="white" />
          {lang === "fr" ? "Voir la démo" : "See demo"}
        </span>
        <Icon className="w-4 h-4" />
      </div>
    </button>
  );
}

export function DemoModal({ open, onClose, variant, title, lang }) {
  const SceneComp = VARIANTS[variant] || DefaultScene;
  useEffect(() => {
    if (!open) return;
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[80] bg-[#1F3A5F]/85 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="demo-modal">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-3xl p-4 md:p-6 shadow-2xl w-full max-w-lg">
            <button
              onClick={onClose}
              data-testid="demo-modal-close"
              aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#FBF7F3] hover:bg-[#E5276A] hover:text-white flex items-center justify-center transition-colors z-10">
              <X className="w-4 h-4" />
            </button>
            <div className="mb-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#E5276A]">
                {lang === "fr" ? "Démo — 15 s" : "Demo — 15 s"}
              </div>
              <h3 className="font-display text-lg text-[#1F3A5F] mt-0.5 pr-8">{title}</h3>
            </div>
            <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-[#FBF7F3]">
              <SceneComp />
            </div>
            <p className="text-[11px] text-[#8B95A1] mt-3 italic">
              {lang === "fr"
                ? "Reconstitution animée du parcours. La vraie UI peut légèrement varier."
                : "Animated reconstruction of the flow. The actual UI may vary slightly."}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export const DEMO_VARIANTS = Object.keys(VARIANTS);
